import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import routes from "constants/routes";
import { COUPON_USAGE_OPERATIONS } from "constants/coupons";
import { mapCouponToFormValues } from "utils/couponUtils";
import { buildEditCouponPayload, mapCouponInfoFromApi } from "helpers/couponApi";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import {
  getCouponInfoQueryKey,
  useCouponInfo,
  useEditCoupon,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Loader/Loader";

import CouponForm from "./CouponForm";
import styles from "./CouponsList.module.scss";

const EditCoupon = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbarContext();
  const { data, isLoading, isError, error } = useCouponInfo(id);
  const { mutateAsync, isPending } = useEditCoupon();

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.emptyState}>
        <p>{formatApiErrorForDisplay(error, "Failed to load coupon details.")}</p>
        <button
          type="button"
          className={styles.createButton}
          onClick={() => navigate(routes.coupons.list)}
        >
          Back to Coupons
        </button>
      </div>
    );
  }

  const handleSubmit = async (values) => {
    try {
      const payload = buildEditCouponPayload(values);
      await mutateAsync({ couponCode: values.code, payload });
      await queryClient.invalidateQueries({
        queryKey: getCouponInfoQueryKey(id),
      });
      openSnackbar(`Coupon "${values.code}" updated successfully`, "success");
    } catch (submitError) {
      openSnackbar(
        formatApiErrorForDisplay(submitError, "Failed to update coupon."),
        "error"
      );
    }
  };

  const initialValues = {
    ...mapCouponToFormValues(mapCouponInfoFromApi(data)),
    usageOperation: COUPON_USAGE_OPERATIONS.INCREASE,
    usageCountChange: 0,
  };

  return (
    <CouponForm
      mode="edit"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
    />
  );
};

export default EditCoupon;
